import React, { useState } from 'react';
import api from '../../utils/axios';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Search, UserRound, CheckSquare, Square } from 'lucide-react';
import { fmtNum, fmtFecha } from './formato';

const FUENTES = { padron: 'Padrón', waya: 'Waya', movimientos: 'Movimientos' };

export const BuscarTitular = ({ titular, setTitular, seleccion, setSeleccion }) => {
  const [q, setQ] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [error, setError] = useState(null);

  const buscar = async () => {
    if (q.replace(/\D/g, '').length < 11) { setError('Ingresá un CUIT/CUIL (11 dígitos) o un CVU (22 dígitos)'); return; }
    setBuscando(true); setError(null);
    try {
      const r = await api.get('/export-movimientos/resolver', { params: { q } });
      setTitular({ ...r.data, objetivo: q });
      setSeleccion(r.data.cuentas.map((c) => c.cvu));
    } catch (e) {
      setTitular(null); setSeleccion([]);
      setError(e.response?.data?.detail || 'No se pudo buscar');
    } finally { setBuscando(false); }
  };

  const toggle = (cvu) => setSeleccion((s) => (s.includes(cvu) ? s.filter((x) => x !== cvu) : [...s, cvu]));

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && buscar()}
          placeholder="CUIT/CUIL (20-12345678-9) o CVU (22 dígitos)"
          className="max-w-md font-mono"
          data-testid="expmov-buscar-input"
        />
        <Button onClick={buscar} disabled={buscando} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="expmov-buscar-btn">
          {buscando ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Search className="h-4 w-4 mr-1.5" />} Buscar
        </Button>
      </div>
      {error && <p className="text-sm text-red-600" data-testid="expmov-buscar-error">{error}</p>}

      {titular && (
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3" data-testid="expmov-titular">
          <div className="flex items-center gap-2 mb-2">
            <UserRound className="h-4 w-4 text-blue-700" />
            <span className="font-semibold text-slate-900 dark:text-white" data-testid="expmov-titular-nombre">{titular.titular || 'Titular sin nombre'}</span>
            {titular.cuit_fmt && <span className="font-mono text-sm text-slate-500">{titular.cuit_fmt}</span>}
            <Badge variant="outline" className="text-[10px]">Buscado por {titular.tipo}</Badge>
          </div>
          {titular.cuentas.length === 0 ? (
            <p className="text-sm text-amber-700">No encontramos cuentas (CVU) para este titular.</p>
          ) : (
            <div className="space-y-1">
              <p className="text-xs text-slate-500">Cuentas que entran en la exportación ({seleccion.length} de {titular.cuentas.length}):</p>
              {titular.cuentas.map((c) => {
                const on = seleccion.includes(c.cvu);
                return (
                  <button
                    key={c.cvu}
                    type="button"
                    onClick={() => toggle(c.cvu)}
                    className="w-full flex flex-wrap items-center gap-x-3 gap-y-1 text-left text-sm px-2 py-1.5 rounded-md hover:bg-slate-50"
                    data-testid={`expmov-cuenta-${c.cvu}`}
                  >
                    {on ? <CheckSquare className="h-4 w-4 text-blue-700" /> : <Square className="h-4 w-4 text-slate-400" />}
                    <span className="font-mono text-slate-800 dark:text-slate-100">{c.cvu}</span>
                    {c.estado && <Badge variant="outline" className="text-[10px]">{c.estado}</Badge>}
                    <span className="text-xs text-slate-500">
                      {c.movimientos ? `${fmtNum(c.movimientos)} movs. (${fmtFecha(c.primer_mov)} a ${fmtFecha(c.ultimo_mov)})` : 'sin movimientos'}
                    </span>
                    <span className="text-[10px] text-slate-400">{c.fuentes.map((f) => FUENTES[f] || f).join(' · ')}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BuscarTitular;
